/**
 * Emoji picker trigger for the message composer.
 *
 * Wraps {@link EmojiPickerPanel} in a Radix Popover with a Smile trigger. The
 * picker bundle is loaded lazily on first open — keeps the inbox initial chunk
 * slim (~20KB gzipped lands only when an agent actually opens the picker). The
 * parent owns cursor-position insertion via the `onSelect` callback so this
 * component stays generic and reusable.
 *
 * The picker itself lives in `emoji-picker-panel.tsx` so the message reaction
 * bar can mount it behind a different trigger without this component growing
 * props for its six call sites.
 */
import { useCallback, useState } from 'react';
import { Smile } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { EmojiPickerPanel } from './emoji-picker-panel';

interface EmojiPickerButtonProps {
    readonly onSelect: (emoji: string) => void;
    readonly disabled?: boolean;
    /**
     * Which side of the trigger the picker opens on. The inbox composer sits at
     * the bottom of the viewport and wants it above; a composer embedded in a
     * form usually has room below. Defaults to the popover's own placement.
     */
    readonly side?: 'top' | 'right' | 'bottom' | 'left';
}

export function EmojiPickerButton({ onSelect, disabled, side }: EmojiPickerButtonProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const handleSelect = useCallback(
        (emoji: string) => {
            onSelect(emoji);
            setOpen(false);
        },
        [onSelect],
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            disabled={disabled}
                            aria-label={t('inbox.composer.emoji_aria', 'Insert emoji')}
                            className="text-text-secondary"
                        >
                            <Smile className="size-4" />
                        </Button>
                    </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>{t('inbox.composer.emoji_aria', 'Insert emoji')}</TooltipContent>
            </Tooltip>
            <PopoverContent
                align="start"
                side={side ?? 'top'}
                sideOffset={8}
                className="w-auto border-0 bg-transparent p-0 shadow-none"
            >
                <EmojiPickerPanel onSelect={handleSelect} />
            </PopoverContent>
        </Popover>
    );
}
